import { useEffect, useRef, useState, type FormEvent } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { api } from "../../../lib/api.js";
import { getSocket } from "../../../lib/socket.js";
import { useAuth } from "../../../lib/auth-context.js";

interface ContactFlag {
  id: string;
  kind: string;
  value: string | null;
  createdAt: string;
}

interface ConversationHeader {
  id: string;
  status: string;
  unreadCount: number;
  lastMessageAt: string | null;
  botPausedUntil: string | null;
  assignedToUserId?: string | null;
  assignedAt?: string | null;
  flags?: ContactFlag[];
  contact: {
    id: string;
    igUsername: string | null;
    name: string | null;
    profilePicUrl: string | null;
  };
  igAccount: {
    id: string;
    igUsername: string;
    profilePicUrl: string | null;
  };
}

interface MessageRow {
  id: string;
  direction: "inbound" | "outbound";
  type: string;
  body: { text?: string; attachments?: Array<{ type: string; payload?: { url?: string } }> };
  status: string;
  createdAt: string;
}

export const Route = createFileRoute("/_app/inbox/$conversationId")({
  component: ConversationDetail,
});

interface SendError {
  kind: "outside_window" | "reauth" | "suspended" | "other";
  message: string;
  lastUserMessageAt?: string | null;
}

interface CannedReply {
  id: string;
  name: string;
  shortcut: string | null;
  body: { text: string };
}

interface Note {
  id: string;
  body: string;
  createdAt: string;
  authorUserId: string | null;
  authorEmail: string | null;
}

type TimelineItem =
  | { kind: "message"; ts: string; data: MessageRow }
  | { kind: "note"; ts: string; data: Note };

interface PresenceMember {
  userId: string;
  email: string;
}

interface TypingPulse {
  email: string;
  ts: number;
}

function ConversationDetail() {
  const { conversationId } = Route.useParams();
  const [header, setHeader] = useState<ConversationHeader | null>(null);
  const [messages, setMessages] = useState<MessageRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sendError, setSendError] = useState<SendError | null>(null);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [cannedReplies, setCannedReplies] = useState<CannedReply[]>([]);
  const [notes, setNotes] = useState<Note[]>([]);
  const [cannedSelectedIdx, setCannedSelectedIdx] = useState(0);
  const [presence, setPresence] = useState<PresenceMember[]>([]);
  const [typers, setTypers] = useState<Record<string, TypingPulse>>({});
  const messagesEndRef = useRef<HTMLLIElement | null>(null);
  const lastTypingEmitAt = useRef(0);
  const { user, workspace, workspaces } = useAuth();
  const currentUserId = user?.id ?? null;
  const currentRole =
    workspaces.find((w) => w.id === workspace?.id)?.role ?? null;
  const isOwnerOrAdmin = currentRole === "owner" || currentRole === "admin";

  // Merge messages + notes by created_at into a single chronological
  // timeline. Notes render distinctly so they're visibly NOT a sent
  // message — same column as inbound bubbles to keep the eye relaxed.
  const timeline: TimelineItem[] = [
    ...messages.map((m) => ({ kind: "message" as const, ts: m.createdAt, data: m })),
    ...notes.map((n) => ({ kind: "note" as const, ts: n.createdAt, data: n })),
  ].sort((a, b) => a.ts.localeCompare(b.ts));

  // Slash-trigger filter: when draft starts with "/", extract the
  // prefix after the slash and match against shortcut + name. Empty
  // prefix ("/") shows all canned replies. Anything else (no leading
  // slash) hides the dropdown.
  const slashMatch = draft.match(/^\/([a-zA-Z0-9_-]*)$/);
  const cannedReplyMatches = slashMatch
    ? filterCannedReplies(cannedReplies, slashMatch[1] ?? "")
    : [];

  // Keep the keyboard selection valid when the filter shrinks. Re-pin
  // to 0 on any new prefix so the user always lands on the top match.
  useEffect(() => {
    setCannedSelectedIdx(0);
  }, [slashMatch?.[1], cannedReplyMatches.length]);

  // Per-agent unread (Cycle 17). Fire-and-forget on conversation open.
  // The inbox-list join uses conversation_reads.last_read_at to decide
  // the unread highlight for the caller; bumping it here clears the
  // highlight on next list refresh without nagging on every interaction.
  useEffect(() => {
    if (!conversationId) return;
    void api
      .patch(`/inbox/conversations/${conversationId}/read`, {})
      .catch(() => {
        /* best-effort; never block the thread render on this */
      });
  }, [conversationId]);

  function pickCannedReply(cr: CannedReply) {
    setDraft(cr.body.text);
  }

  function onComposerKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (cannedReplyMatches.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setCannedSelectedIdx((i) => Math.min(i + 1, cannedReplyMatches.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setCannedSelectedIdx((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      // Insert the highlighted reply instead of submitting the form.
      const pick = cannedReplyMatches[cannedSelectedIdx];
      if (pick) {
        e.preventDefault();
        pickCannedReply(pick);
      }
    } else if (e.key === "Escape") {
      e.preventDefault();
      setDraft("");
    }
  }

  useEffect(() => {
    void load();
  }, [conversationId]);

  // Canned replies are workspace-scoped; load once on mount and keep
  // through conversation switches. A future "saved a new one" event
  // would need to refetch — for v1, the agent can refresh the page.
  useEffect(() => {
    api
      .get<{ cannedReplies: CannedReply[] }>("/canned-replies")
      .then((r) => setCannedReplies(r.data.cannedReplies))
      .catch(() => {
        // Silent — composer still works without canned replies.
      });
  }, []);

  // Per-conversation presence: emit join on mount, leave on unmount.
  // Listen for presence + typing events scoped to this conversation.
  // The server filters self out of typing broadcasts; we filter self
  // out of presence client-side (so an agent doesn't see themselves
  // as a viewer).
  const userEmail = user?.email ?? "";
  useEffect(() => {
    if (!userEmail) return;
    const sock = getSocket();
    sock.emit("conversation:join", { conversationId, email: userEmail });
    const onPresence = (p: { conversationId: string; members: PresenceMember[] }) => {
      if (p.conversationId !== conversationId) return;
      setPresence(p.members);
    };
    const onTyping = (p: {
      conversationId: string;
      userId: string;
      email: string;
      isTyping: boolean;
    }) => {
      if (p.conversationId !== conversationId) return;
      setTypers((prev) => {
        const next = { ...prev };
        if (p.isTyping) {
          next[p.userId] = { email: p.email, ts: Date.now() };
        } else {
          delete next[p.userId];
        }
        return next;
      });
    };
    sock.on("conversation:presence", onPresence);
    sock.on("conversation:typing", onTyping);
    return () => {
      sock.emit("conversation:leave", { conversationId });
      sock.off("conversation:presence", onPresence);
      sock.off("conversation:typing", onTyping);
      setPresence([]);
      setTypers({});
    };
  }, [conversationId, userEmail]);

  // Decay stale typing pulses every 2s — if a teammate's tab crashed
  // mid-type without sending a {isTyping:false}, their indicator
  // disappears within ~5s instead of sticking forever.
  useEffect(() => {
    const i = setInterval(() => {
      setTypers((prev) => {
        const cutoff = Date.now() - 5000;
        const next: typeof prev = {};
        for (const [uid, t] of Object.entries(prev)) {
          if (t.ts >= cutoff) next[uid] = t;
        }
        return Object.keys(next).length === Object.keys(prev).length ? prev : next;
      });
    }, 2000);
    return () => clearInterval(i);
  }, []);

  // Emit typing pulses while the agent is composing. Throttle to one
  // {isTyping:true} per 1.5s so a fast typist (~5 keys/sec) doesn't
  // hammer the realtime pod with redundant emits — the receiver can't
  // tell the difference. {isTyping:false} on draft empty / 3s idle is
  // never throttled because dropping a stop-pulse would leave the
  // indicator stuck on the receiver.
  useEffect(() => {
    if (!userEmail) return;
    const sock = getSocket();
    if (draft.length === 0) {
      sock.emit("conversation:typing", {
        conversationId,
        email: userEmail,
        isTyping: false,
      });
      lastTypingEmitAt.current = 0;
      return;
    }
    const now = Date.now();
    if (now - lastTypingEmitAt.current >= 1500) {
      sock.emit("conversation:typing", {
        conversationId,
        email: userEmail,
        isTyping: true,
      });
      lastTypingEmitAt.current = now;
    }
    const stopTimer = setTimeout(() => {
      sock.emit("conversation:typing", {
        conversationId,
        email: userEmail,
        isTyping: false,
      });
      lastTypingEmitAt.current = 0;
    }, 3000);
    return () => clearTimeout(stopTimer);
  }, [draft, conversationId, userEmail]);

  // Realtime: refresh on any message event for this conversation. The
  // server only emits to our workspace's room (cookie-auth on connect),
  // so we just filter by conversationId here. Note add/remove events
  // patch the local notes list directly — no full reload needed since
  // we have the row payload inline.
  useEffect(() => {
    const sock = getSocket();
    const onEvent = (payload: { conversationId?: string }) => {
      if (payload?.conversationId === conversationId) void load();
    };
    const onNoteAdded = (payload: { conversationId?: string; note?: Note }) => {
      if (payload?.conversationId !== conversationId || !payload.note) return;
      setNotes((cur) => {
        // Dedupe — if the local optimistic insert (from addNote) already
        // appended this id, the broadcast is a no-op for this tab.
        if (cur.some((n) => n.id === payload.note!.id)) return cur;
        return [...cur, payload.note!];
      });
    };
    const onNoteRemoved = (payload: { conversationId?: string; noteId?: string }) => {
      if (payload?.conversationId !== conversationId || !payload.noteId) return;
      setNotes((cur) => cur.filter((n) => n.id !== payload.noteId));
    };
    sock.on("conversation.message_received", onEvent);
    sock.on("conversation.message_sent", onEvent);
    sock.on("conversation.note_added", onNoteAdded);
    sock.on("conversation.note_removed", onNoteRemoved);
    return () => {
      sock.off("conversation.message_received", onEvent);
      sock.off("conversation.message_sent", onEvent);
      sock.off("conversation.note_added", onNoteAdded);
      sock.off("conversation.note_removed", onNoteRemoved);
    };
  }, [conversationId]);

  // Scroll new timeline items into view (messages OR notes).
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages.length, notes.length]);

  async function sendMessage(text: string, opts?: { tag?: "HUMAN_AGENT" }) {
    if (sending) return;
    setSending(true);
    setSendError(null);
    const optimisticId = `optimistic-${Date.now()}`;
    const idempotencyKey = crypto.randomUUID();
    setMessages((prev) => [
      ...prev,
      {
        id: optimisticId,
        direction: "outbound",
        type: "text",
        body: { text },
        status: "queued",
        createdAt: new Date().toISOString(),
      },
    ]);
    try {
      await api.post(`/inbox/conversations/${conversationId}/send`, {
        text,
        idempotencyKey,
        tag: opts?.tag,
      });
      await load();
    } catch (err: unknown) {
      type ApiErr = { response?: { status?: number; data?: { message?: string; error?: string; lastUserMessageAt?: string | null } } };
      const data = (err as ApiErr).response?.data;
      const status = (err as ApiErr).response?.status;
      // Restore the draft + remove the optimistic bubble.
      setDraft(text);
      setMessages((prev) => prev.filter((m) => m.id !== optimisticId));
      if (status === 422 && data?.message === "outside_messaging_window") {
        setSendError({
          kind: "outside_window",
          message: data.error ?? "This conversation is outside the 24h reply window.",
          lastUserMessageAt: data.lastUserMessageAt ?? null,
        });
      } else if (status === 422 && data?.message === "reauth_required") {
        setSendError({ kind: "reauth", message: data.error ?? "Reconnect this Instagram account." });
      } else if (status === 422 && data?.message === "account_suspended") {
        setSendError({ kind: "suspended", message: data.error ?? "Account is suspended or disconnected." });
      } else {
        setSendError({ kind: "other", message: "Send failed. Try again." });
      }
    } finally {
      setSending(false);
    }
  }

  async function handleSend(e: FormEvent) {
    e.preventDefault();
    const text = draft.trim();
    if (!text) return;
    setDraft("");
    await sendMessage(text);
  }

  async function handleSendAsHumanAgent() {
    const text = draft.trim();
    if (!text) return;
    setDraft("");
    await sendMessage(text, { tag: "HUMAN_AGENT" });
  }

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get<{
        conversation: ConversationHeader;
        messages: MessageRow[];
      }>(`/inbox/conversations/${conversationId}/messages`);
      setHeader(res.data.conversation);
      setMessages(res.data.messages);
      // Notes load in parallel; failure is non-fatal so the timeline
      // still renders messages even if /notes is misbehaving.
      try {
        const nres = await api.get<{ notes: Note[] }>(
          `/inbox/conversations/${conversationId}/notes`,
        );
        setNotes(nres.data.notes);
      } catch {
        setNotes([]);
      }
    } catch {
      setError("Failed to load conversation.");
    } finally {
      setLoading(false);
    }
  }

  async function addNote(text: string) {
    if (!text.trim()) return;
    // Composer footgun guard: long drafts are more likely to be a real
    // reply that the agent meant to send. Confirm before silently
    // converting it into a private note.
    if (
      text.length > 80 &&
      !confirm(
        "This text is long for a private note. Add it as a workspace-only note (NOT sent to the contact)?",
      )
    ) {
      return;
    }
    try {
      const res = await api.post<{ note: Note }>(
        `/inbox/conversations/${conversationId}/notes`,
        { text },
      );
      setNotes((cur) => [...cur, res.data.note]);
      setDraft("");
    } catch {
      setError("Failed to add note.");
    }
  }

  async function deleteNote(noteId: string) {
    if (!confirm("Delete this note?")) return;
    try {
      await api.delete(`/inbox/conversations/${conversationId}/notes/${noteId}`);
      setNotes((cur) => cur.filter((n) => n.id !== noteId));
    } catch {
      setError("Failed to delete note.");
    }
  }

  return (
    <main style={{ maxWidth: 760, margin: "0 auto", padding: "16px 24px" }}>
      <div style={{ marginBottom: 16 }}>
        <Link to="/inbox" className="brut-link-btn" style={{ fontSize: 14 }}>
          ← All conversations
        </Link>
      </div>

      {error && <div className="brut-error">{error}</div>}

      {loading || !header ? (
        <p>Loading…</p>
      ) : (
        <>
          <header
            className="brut-card"
            style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 24 }}
          >
            {header.contact.profilePicUrl ? (
              <img
                src={header.contact.profilePicUrl}
                alt=""
                width={56}
                height={56}
                style={{ border: "var(--border)" }}
              />
            ) : (
              <div style={{ width: 56, height: 56, border: "var(--border)", background: "var(--accent)" }} />
            )}
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 800, fontSize: 20 }}>
                {displayName(header.contact)}
              </div>
              <div style={{ fontSize: 13, color: "var(--muted)" }}>
                via @{header.igAccount.igUsername}
              </div>
              {isBotCurrentlyPaused(header.botPausedUntil) ? (
                <div style={{ fontSize: 12, color: "#b40000", marginTop: 4 }}>
                  Bot paused until {new Date(header.botPausedUntil!).toLocaleString()}
                </div>
              ) : null}
              {(header.flags ?? []).length > 0 && (
                <div
                  style={{
                    display: "flex",
                    gap: 6,
                    flexWrap: "wrap",
                    alignItems: "center",
                    marginTop: 4,
                  }}
                >
                  {(header.flags ?? []).map((f) => (
                    <span
                      key={f.id}
                      title={`Opened ${new Date(f.createdAt).toLocaleString()}`}
                      style={{
                        fontSize: 11,
                        padding: "2px 6px",
                        background: "#ffd7d7",
                        border: "var(--border)",
                        fontWeight: 700,
                      }}
                    >
                      ⚠ {f.kind === "abuse_rate" ? `abuse rate (${f.value ?? "?"})` : f.kind}
                    </span>
                  ))}
                  <button
                    type="button"
                    className="brut-button is-secondary"
                    style={{ fontSize: 11, padding: "2px 8px" }}
                    onClick={async () => {
                      try {
                        await api.patch(
                          `/inbox/conversations/${conversationId}/flags/resolve`,
                          {},
                        );
                        setHeader((h) => (h ? { ...h, flags: [] } : h));
                      } catch {
                        /* swallow — refresh on next load */
                      }
                    }}
                  >
                    Dismiss
                  </button>
                </div>
              )}
              {(() => {
                // Filter self out so the agent doesn't see "you viewing".
                const others = presence.filter((p) => p.userId !== currentUserId);
                if (others.length === 0) return null;
                return (
                  <div
                    style={{ fontSize: 12, color: "var(--muted)", marginTop: 4 }}
                    title={others.map((o) => o.email).join(", ")}
                  >
                    👀 {formatViewers(others)}
                  </div>
                );
              })()}
            </div>
            <AssignControl
              header={header}
              currentUserId={currentUserId}
              onChanged={(updated) =>
                setHeader((h) => (h ? { ...h, ...updated } : h))
              }
            />
            <BotPauseControl
              header={header}
              onChanged={(updated) =>
                setHeader((h) => (h ? { ...h, ...updated } : h))
              }
            />
            <button className="brut-button is-secondary" onClick={() => void load()}>
              Refresh
            </button>
          </header>

          <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "grid", gap: 8 }}>
            {timeline.map((item) => {
              if (item.kind === "message") {
                const m = item.data;
                return (
                  <li
                    key={`m-${m.id}`}
                    style={{
                      display: "flex",
                      justifyContent: m.direction === "inbound" ? "flex-start" : "flex-end",
                    }}
                  >
                    <div
                      style={{
                        maxWidth: "70%",
                        border: "var(--border)",
                        padding: "10px 14px",
                        background:
                          m.direction === "inbound" ? "var(--paper)" : "var(--accent)",
                        boxShadow: "var(--shadow-sm)",
                        opacity: m.status === "queued" ? 0.6 : 1,
                      }}
                    >
                      <div style={{ fontSize: 15 }}>{renderBody(m)}</div>
                      <div
                        style={{
                          fontSize: 11,
                          color: "var(--muted)",
                          marginTop: 6,
                          display: "flex",
                          gap: 8,
                        }}
                      >
                        <span>{new Date(m.createdAt).toLocaleString()}</span>
                        {m.direction === "outbound" &&
                          m.status !== "sent" &&
                          m.status !== "delivered" && <span>· {m.status}</span>}
                      </div>
                    </div>
                  </li>
                );
              }
              const n = item.data;
              const canDelete =
                isOwnerOrAdmin || (currentUserId && n.authorUserId === currentUserId);
              return (
                <li
                  key={`n-${n.id}`}
                  style={{ display: "flex", justifyContent: "center" }}
                >
                  <div
                    style={{
                      maxWidth: "85%",
                      border: "var(--border)",
                      padding: "8px 12px",
                      background: "#fff8d8",
                      boxShadow: "var(--shadow-sm)",
                    }}
                  >
                    <div
                      style={{
                        fontSize: 11,
                        color: "var(--muted)",
                        marginBottom: 4,
                        display: "flex",
                        justifyContent: "space-between",
                        gap: 8,
                      }}
                    >
                      <span>
                        📝 Note from{" "}
                        <strong>{n.authorEmail ?? "Former teammate"}</strong> ·{" "}
                        {new Date(n.createdAt).toLocaleString()}
                      </span>
                      {canDelete && (
                        <button
                          type="button"
                          onClick={() => void deleteNote(n.id)}
                          style={{
                            background: "transparent",
                            border: "none",
                            color: "var(--muted)",
                            cursor: "pointer",
                            font: "inherit",
                            fontSize: 11,
                          }}
                          title="Delete note"
                        >
                          ✕
                        </button>
                      )}
                    </div>
                    <div style={{ fontSize: 14, whiteSpace: "pre-wrap" }}>{n.body}</div>
                  </div>
                </li>
              );
            })}
            <li ref={messagesEndRef} />
          </ul>

          {timeline.length === 0 && (
            <div className="brut-card">
              <p style={{ margin: 0 }}>No messages in this conversation yet.</p>
            </div>
          )}

          {sendError && (
            <div
              className="brut-error"
              style={{ marginTop: 24, marginBottom: 0 }}
            >
              <div>{sendError.message}</div>
              {sendError.kind === "outside_window" && sendError.lastUserMessageAt && (
                <div style={{ fontWeight: 400, marginTop: 6, fontSize: 13 }}>
                  Last user message: {new Date(sendError.lastUserMessageAt).toLocaleString()}.
                  Send as <strong>HUMAN_AGENT</strong> if a human agent is replying
                  (Meta's Human Agent permission required).
                </div>
              )}
              {sendError.kind === "outside_window" && (
                <div style={{ marginTop: 8 }}>
                  <button
                    type="button"
                    className="brut-button is-secondary"
                    disabled={sending || draft.trim().length === 0}
                    onClick={() => void handleSendAsHumanAgent()}
                  >
                    Send as HUMAN_AGENT
                  </button>
                </div>
              )}
            </div>
          )}

          {(() => {
            const otherTypers = Object.entries(typers).filter(
              ([uid]) => uid !== currentUserId,
            );
            if (otherTypers.length === 0) return null;
            return (
              <div
                style={{
                  marginTop: 12,
                  fontSize: 12,
                  color: "var(--muted)",
                  fontStyle: "italic",
                }}
                aria-live="polite"
              >
                {formatTypers(otherTypers.map(([, t]) => t.email))} typing…
              </div>
            );
          })()}

          {cannedReplyMatches.length > 0 && (
            <div
              className="brut-card"
              role="listbox"
              aria-label="Canned reply suggestions"
              id="canned-replies-listbox"
              style={{
                marginTop: 12,
                padding: 0,
                maxHeight: 240,
                overflowY: "auto",
              }}
            >
              {cannedReplyMatches.map((cr, idx) => {
                const selected = idx === cannedSelectedIdx;
                return (
                  <button
                    type="button"
                    key={cr.id}
                    id={`canned-reply-opt-${idx}`}
                    role="option"
                    aria-selected={selected}
                    onClick={() => pickCannedReply(cr)}
                    onMouseEnter={() => setCannedSelectedIdx(idx)}
                    style={{
                      display: "block",
                      width: "100%",
                      textAlign: "left",
                      padding: "8px 12px",
                      background: selected ? "var(--accent)" : "transparent",
                      border: "none",
                      borderBottom: "var(--border)",
                      cursor: "pointer",
                      font: "inherit",
                    }}
                  >
                    <div style={{ fontWeight: 700, fontSize: 13 }}>
                      {cr.shortcut && (
                        <span style={{ color: "var(--muted)" }}>/{cr.shortcut} · </span>
                      )}
                      {cr.name}
                    </div>
                    <div
                      style={{
                        fontSize: 12,
                        color: "var(--muted)",
                        marginTop: 2,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {cr.body.text}
                    </div>
                  </button>
                );
              })}
            </div>
          )}

          <form
            onSubmit={handleSend}
            style={{
              marginTop: sendError ? 12 : 24,
              display: "flex",
              gap: 8,
              flexWrap: "wrap",
            }}
          >
            <input
              type="text"
              className="brut-input"
              placeholder="Type a reply… (or / to insert a canned reply)"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={onComposerKeyDown}
              disabled={sending}
              maxLength={2000}
              // Make sure the input keeps a usable width even when
              // the row wraps onto two lines on a narrow viewport.
              style={{ flex: "1 1 200px", minWidth: 0 }}
              role="combobox"
              aria-autocomplete="list"
              aria-expanded={cannedReplyMatches.length > 0}
              aria-controls="canned-replies-listbox"
              aria-activedescendant={
                cannedReplyMatches.length > 0
                  ? `canned-reply-opt-${cannedSelectedIdx}`
                  : undefined
              }
            />
            <button
              type="submit"
              className="brut-button"
              disabled={sending || draft.trim().length === 0}
            >
              {sending ? "Sending…" : "Send"}
            </button>
            <button
              type="button"
              className="brut-button is-secondary"
              disabled={draft.trim().length === 0}
              onClick={() => void addNote(draft)}
              title="Workspace-private — never sent to the contact"
            >
              📝 Add note
            </button>
          </form>

          <div style={{ marginTop: 8, fontSize: 11, color: "var(--muted)" }}>
            Sends within 24h of the user's last message land directly.
            Outside that window, Meta requires the HUMAN_AGENT tag — only
            use it for actual human-agent replies.
          </div>
        </>
      )}
    </main>
  );
}

function formatViewers(viewers: PresenceMember[]): string {
  if (viewers.length === 1) return `${viewers[0]!.email} viewing`;
  if (viewers.length === 2) return `${viewers[0]!.email} and ${viewers[1]!.email} viewing`;
  return `${viewers[0]!.email} and ${viewers.length - 1} others viewing`;
}

function formatTypers(emails: string[]): string {
  if (emails.length === 1) return `${emails[0]!} is`;
  if (emails.length === 2) return `${emails[0]!} and ${emails[1]!} are`;
  return `${emails[0]!} and ${emails.length - 1} others are`;
}

function filterCannedReplies(replies: CannedReply[], prefix: string): CannedReply[] {
  // Empty prefix → show everything (cap at 8 to keep the popover sane).
  // Non-empty → match shortcut OR name (case-insensitive prefix on
  // shortcut, case-insensitive substring on name). Shortcut hits sort
  // first so a typed `/refund` lands the refund-shortcut row at the top
  // even if multiple names mention "refund".
  const p = prefix.toLowerCase();
  if (!p) return replies.slice(0, 8);
  const shortcutHits: CannedReply[] = [];
  const nameHits: CannedReply[] = [];
  for (const r of replies) {
    const sc = r.shortcut?.toLowerCase();
    if (sc && sc.startsWith(p)) shortcutHits.push(r);
    else if (r.name.toLowerCase().includes(p)) nameHits.push(r);
  }
  return [...shortcutHits, ...nameHits].slice(0, 8);
}

function displayName(contact: { id: string; name: string | null; igUsername: string | null }): string {
  if (contact.name) return contact.name;
  if (contact.igUsername) return `@${contact.igUsername}`;
  return `Instagram user · ${contact.id.slice(0, 6)}`;
}

function renderBody(m: MessageRow): React.ReactNode {
  if (m.body.text) return m.body.text;
  if (m.body.attachments?.[0]) {
    const a = m.body.attachments[0];
    return <em>[{a.type} attachment{a.payload?.url ? ` — ${a.payload.url}` : ""}]</em>;
  }
  return <em>[{m.type}]</em>;
}

function isBotCurrentlyPaused(until: string | null | undefined): boolean {
  if (!until) return false;
  return new Date(until).getTime() > Date.now();
}

function AssignControl({
  header,
  currentUserId,
  onChanged,
}: {
  header: ConversationHeader;
  currentUserId: string | null;
  onChanged: (next: {
    status: string;
    assignedToUserId: string | null;
    assignedAt: string | null;
  }) => void;
}) {
  const [busy, setBusy] = useState(false);
  const isMine = header.assignedToUserId && header.assignedToUserId === currentUserId;
  const isAssigned = Boolean(header.assignedToUserId);

  async function patch(userId: "me" | null) {
    if (busy) return;
    setBusy(true);
    try {
      const r = await api.patch<{
        conversation: {
          id: string;
          status: string;
          assignedToUserId: string | null;
          assignedAt: string | null;
        };
      }>(`/inbox/conversations/${header.id}/assign`, { userId });
      onChanged({
        status: r.data.conversation.status,
        assignedToUserId: r.data.conversation.assignedToUserId,
        assignedAt: r.data.conversation.assignedAt,
      });
    } catch {
      // Surface via the parent's error state would be nicer; for v1
      // a silent fail + button stays clickable is acceptable.
    } finally {
      setBusy(false);
    }
  }

  if (isMine) {
    return (
      <button
        type="button"
        disabled={busy}
        className="brut-button is-secondary"
        onClick={() => void patch(null)}
        title="Unassign yourself"
      >
        ✓ Mine
      </button>
    );
  }
  if (isAssigned) {
    return (
      <button
        type="button"
        disabled={busy}
        className="brut-button is-secondary"
        onClick={() => void patch("me")}
        title="Take over from current assignee"
      >
        Take over
      </button>
    );
  }
  return (
    <button
      type="button"
      disabled={busy}
      className="brut-button"
      onClick={() => void patch("me")}
    >
      Assign me
    </button>
  );
}

function BotPauseControl({
  header,
  onChanged,
}: {
  header: ConversationHeader;
  onChanged: (next: { status: string; botPausedUntil: string | null }) => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const paused = isBotCurrentlyPaused(header.botPausedUntil);

  async function setPause(hours: number | null) {
    setBusy(true);
    setError(null);
    try {
      const until = hours == null ? null : new Date(Date.now() + hours * 3_600_000).toISOString();
      const r = await api.patch<{
        conversation: { id: string; status: string; botPausedUntil: string | null };
      }>(`/inbox/conversations/${header.id}/bot-pause`, { until });
      onChanged({
        status: r.data.conversation.status,
        botPausedUntil: r.data.conversation.botPausedUntil,
      });
    } catch (err) {
      const msg = (err as { response?: { data?: { message?: string } } }).response
        ?.data?.message;
      setError(msg ?? "Failed to update");
    } finally {
      setBusy(false);
    }
  }

  if (paused) {
    return (
      <div style={{ display: "grid", gap: 4 }}>
        <button
          className="brut-button"
          onClick={() => void setPause(null)}
          disabled={busy}
          style={{ background: "#b40000", color: "white" }}
        >
          {busy ? "..." : "Resume bot"}
        </button>
        {error ? <div style={{ fontSize: 12, color: "#b40000" }}>{error}</div> : null}
      </div>
    );
  }
  return (
    <div style={{ display: "grid", gap: 4 }}>
      <button
        className="brut-button is-secondary"
        onClick={() => void setPause(24)}
        disabled={busy}
        title="Stop auto-replies on this conversation for 24h"
      >
        {busy ? "..." : "Pause bot 24h"}
      </button>
      {error ? <div style={{ fontSize: 12, color: "#b40000" }}>{error}</div> : null}
    </div>
  );
}
